/**
 * Condition Analyzer Core Algorithm
 * 
 * Pure TypeScript implementation - no Angular dependencies.
 * Used by both frontend service and backend scripts.
 */

import {
    AnalysisMetrics,
    AnalyzerDataPoint,
    Condition,
    ConditionAnalysisResult,
    ConditionAnalyzerConfig,
    ConditionFactor,
    CONDITION_INFO,
    DEFAULT_ANALYZER_CONFIG,
    TrendDirection
} from './condition-analyzer.models';

/**
 * Core analyzer class - framework agnostic
 */
export class ConditionAnalyzerCore {

    private config: ConditionAnalyzerConfig;

    constructor(config?: Partial<ConditionAnalyzerConfig>) {
        this.config = { ...DEFAULT_ANALYZER_CONFIG, ...config };
    }

    /**
     * Update the analyzer configuration
     */
    setConfig(config: Partial<ConditionAnalyzerConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * Get current configuration
     */
    getConfig(): ConditionAnalyzerConfig {
        return { ...this.config };
    }

    /**
     * Main analysis method - analyzes data points and returns the detected condition
     */
    analyze(dataPoints: AnalyzerDataPoint[]): ConditionAnalysisResult {
        // Validate input
        if (!dataPoints || dataPoints.length < this.config.minDataPoints) {
            return this.createUnknownResult(dataPoints, 'Insufficient data points for analysis');
        }

        // Calculate all metrics
        const metrics = this.calculateMetrics(dataPoints);

        // Collect condition factors
        const factors = this.collectFactors(metrics, dataPoints);

        // Determine trend direction
        const trendDirection = this.determineTrendDirection(metrics);

        // Detect the condition
        const { condition, confidence, explanation } = this.detectCondition(metrics, factors, trendDirection, dataPoints);

        // Generate trend line for visualization
        const trendLine = this.generateTrendLine(dataPoints, metrics);

        return {
            condition,
            conditionInfo: CONDITION_INFO[condition],
            confidence,
            trendDirection,
            metrics,
            explanation,
            factors,
            dataPoints,
            trendLine
        };
    }

    /**
     * Calculate all statistical metrics from the data
     */
    private calculateMetrics(dataPoints: AnalyzerDataPoint[]): AnalysisMetrics {
        const values = dataPoints.map(d => d.value);
        const n = values.length;

        // Basic statistics
        const sum = values.reduce((a, b) => a + b, 0);
        const mean = sum / n;
        
        const sortedValues = [...values].sort((a, b) => a - b);
        const median = n % 2 === 0 
            ? (sortedValues[n/2 - 1] + sortedValues[n/2]) / 2 
            : sortedValues[Math.floor(n/2)];

        const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
        const variance = squaredDiffs.reduce((a, b) => a + b, 0) / n;
        const standardDeviation = Math.sqrt(variance);
        const coefficientOfVariation = mean !== 0 ? standardDeviation / mean : 0;

        // Range
        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;

        // Change metrics
        const startValue = values[0];
        const endValue = values[n - 1];
        const absoluteChange = endValue - startValue;
        const percentChange = startValue !== 0 ? absoluteChange / startValue : 0;
        const rateOfChange = absoluteChange / (n - 1);

        // Linear regression
        const { slope, intercept, rSquared } = this.linearRegression(dataPoints);

        // Position relative to baseline
        const distanceFromBaseline = mean - this.config.baseline;
        const percentOfMax = max !== 0 ? mean / max : 0;

        // Find lowest and highest points
        const lowestPointIndex = values.indexOf(min);
        const highestPointIndex = values.indexOf(max);

        // Detect recovery pattern
        const { hasRecovery, recoveryStartIndex, recoveryPercent } = 
            this.detectRecoveryPattern(values, lowestPointIndex);

        // Calculate volatility score (0-1)
        const volatilityScore = this.calculateVolatility(values, mean);

        // Calculate visual angle of the trend line (in degrees)
        const dataPointsForAngle = values.map((v, i) => ({ date: new Date(), value: v, index: i }));
        const visualAngle = this.calculateVisualAngle(slope, dataPointsForAngle);

        return {
            slope,
            intercept,
            rSquared,
            mean,
            median,
            variance,
            standardDeviation,
            coefficientOfVariation,
            min,
            max,
            range,
            startValue,
            endValue,
            absoluteChange,
            percentChange,
            rateOfChange,
            distanceFromBaseline,
            percentOfMax,
            hasRecovery,
            recoveryStartIndex,
            recoveryPercent,
            lowestPointIndex,
            highestPointIndex,
            volatilityScore,
            visualAngle
        };
    }

    /**
     * Perform linear regression to find slope and fit
     */
    private linearRegression(dataPoints: AnalyzerDataPoint[]): { slope: number; intercept: number; rSquared: number } {
        const n = dataPoints.length;
        const values = dataPoints.map(d => d.value);
        
        // Use index as x-values for simplicity
        let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
        
        for (let i = 0; i < n; i++) {
            sumX += i;
            sumY += values[i];
            sumXY += i * values[i];
            sumX2 += i * i;
        }

        const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;

        // Calculate R-squared
        const yMean = sumY / n;
        let ssRes = 0, ssTot = 0;
        
        for (let i = 0; i < n; i++) {
            const yPred = slope * i + intercept;
            ssRes += Math.pow(values[i] - yPred, 2);
            ssTot += Math.pow(values[i] - yMean, 2);
        }

        const rSquared = ssTot !== 0 ? 1 - (ssRes / ssTot) : 0;

        return { slope, intercept, rSquared };
    }

    /**
     * Detect if there's a recovery pattern (drop then rise)
     */
    private detectRecoveryPattern(values: number[], lowestIndex: number): {
        hasRecovery: boolean;
        recoveryStartIndex: number | null;
        recoveryPercent: number;
    } {
        const n = values.length;
        
        // Recovery requires the lowest point to be in the middle-ish of the data
        // Not at the start (that's just low) and not at the end (that's just danger)
        if (lowestIndex < 2 || lowestIndex > n - 3) {
            return { hasRecovery: false, recoveryStartIndex: null, recoveryPercent: 0 };
        }

        // Check if there was a decline before the lowest point
        const preLowestValues = values.slice(0, lowestIndex + 1);
        const preLowestSlope = this.simpleSlope(preLowestValues);
        
        // Check if there's an increase after the lowest point
        const postLowestValues = values.slice(lowestIndex);
        const postLowestSlope = this.simpleSlope(postLowestValues);

        // Recovery = decline before, rise after
        const hasRecovery = preLowestSlope < -0.5 && postLowestSlope > 0.5;

        if (!hasRecovery) {
            return { hasRecovery: false, recoveryStartIndex: null, recoveryPercent: 0 };
        }

        // Calculate how much recovery occurred
        const lowestValue = values[lowestIndex];
        const peakBeforeDrop = Math.max(...preLowestValues);
        const currentValue = values[n - 1];
        
        const dropAmount = peakBeforeDrop - lowestValue;
        const recoveryAmount = currentValue - lowestValue;
        const recoveryPercent = dropAmount !== 0 ? recoveryAmount / dropAmount : 0;

        return {
            hasRecovery: true,
            recoveryStartIndex: lowestIndex,
            recoveryPercent
        };
    }

    /**
     * Detect Non-Existence crash pattern: sharp crash then flatline near zero
     * 
     * This is NOT a gradual decline (Danger) - it's a sudden crash that happens
     * over a short period, then stays near zero.
     * 
     * Pattern: [normal activity] → [CRASH] → [flatline near zero]
     */
    private detectNonExistenceCrash(values: number[]): {
        isNonExistence: boolean;
        crashStartIndex: number | null;
        crashEndIndex: number | null;
        peakValue: number;
        crashSteepness: number;
    } {
        const n = values.length;
        
        // Need at least 5 points to detect crash pattern
        if (n < 5) {
            return { isNonExistence: false, crashStartIndex: null, crashEndIndex: null, peakValue: 0, crashSteepness: 0 };
        }
        
        // Find the highest point
        const peakValue = Math.max(...values);
        const peakIndex = values.indexOf(peakValue);
        
        // Peak needs to have been reasonably high (above emergency levels)
        if (peakValue < 20) {
            return { isNonExistence: false, crashStartIndex: null, crashEndIndex: null, peakValue, crashSteepness: 0 };
        }
        
        // Check if ending is flatlined near zero
        const endingStart = Math.floor(n * 0.7); // Last 30% (was 40%, too much crash carryover)
        const endingValues = values.slice(endingStart);
        const endingMean = endingValues.reduce((a, b) => a + b, 0) / endingValues.length;
        const endingMax = Math.max(...endingValues);
        const endingStdDev = Math.sqrt(endingValues.reduce((sum, v) => sum + Math.pow(v - endingMean, 2), 0) / endingValues.length);
        
        // Ending must be near zero, reasonably flat, and low values
        const isNearZero = endingMean < this.config.nonExistenceThreshold;
        const isReasonablyFlat = endingStdDev < 5; // Allow some variance (was 3, too strict)
        const mostlyLow = endingMax < this.config.nonExistenceThreshold * 3; // Allow some crash carryover
        
        if (!isNearZero || !isReasonablyFlat || !mostlyLow) {
            return { isNonExistence: false, crashStartIndex: null, crashEndIndex: null, peakValue, crashSteepness: 0 };
        }
        
        // Check if crash was steep (happened over a SHORT period, not gradual)
        // Find where values first drop below the threshold
        let crashEndIndex = n - 1;
        for (let i = peakIndex; i < n; i++) {
            if (values[i] < this.config.nonExistenceThreshold) {
                crashEndIndex = i;
                break;
            }
        }
        
        // Calculate how steep the crash was (points from peak to crash)
        const crashDuration = crashEndIndex - peakIndex;
        const crashRatio = crashDuration / n; // What fraction of the timeline was the crash?
        
        // Crash should be reasonably quick (less than 60% of timeline) AND significant drop
        // A very gradual decline over 80%+ of the timeline is DANGER, not Non-Existence
        const dropPercent = (peakValue - endingMean) / peakValue;
        const crashSteepness = dropPercent / crashRatio; // Higher = steeper crash
        
        // Non-Existence requires ACTIVITY before the crash:
        // - Peak somewhere in the data (not at very beginning index 0)
        // - There was oscillation/activity before or up to the peak
        const peakNotAtVeryStart = peakIndex >= 1; // At least 1 point before peak
        
        // Check for variance/activity in the first half (was there real activity?)
        const firstHalf = values.slice(0, Math.floor(n * 0.5));
        const firstHalfMean = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
        const firstHalfStdDev = Math.sqrt(firstHalf.reduce((sum, v) => sum + Math.pow(v - firstHalfMean, 2), 0) / firstHalf.length);
        const firstHalfCV = firstHalfMean !== 0 ? firstHalfStdDev / firstHalfMean : 0;
        
        // Activity can be:
        // 1. Oscillation (CV > 0.15) - ups and downs
        // 2. Started low and climbed up - growth phase
        // 3. High plateau (mean > 50% of peak) - sustained activity that then crashed
        const hasOscillation = firstHalfCV > 0.15;
        const startedLowClimbedUp = values[0] < peakValue * 0.8; // Started below 80% of peak
        const wasHighPlateau = firstHalfMean > peakValue * 0.7 && firstHalfCV < 0.2; // High stable activity
        const hasActivityBeforeCrash = peakNotAtVeryStart && (hasOscillation || startedLowClimbedUp || wasHighPlateau);
        
        // If no activity detected (straight decline from peak at start), not Non-Existence
        if (!hasActivityBeforeCrash) {
            return { isNonExistence: false, crashStartIndex: null, crashEndIndex: null, peakValue, crashSteepness: 0 };
        }
        
        const isReasonablyFastCrash = crashRatio < 0.6; // Happens within 60% of timeline
        const isMajorDrop = dropPercent > 0.8; // Dropped 80%+ from peak
        const flatlineIsNearZero = endingMean < this.config.nonExistenceThreshold * 1.5; // Really near zero
        
        const isNonExistence = hasActivityBeforeCrash && isReasonablyFastCrash && isMajorDrop && flatlineIsNearZero && crashSteepness > 1.5;
        
        return {
            isNonExistence,
            crashStartIndex: peakIndex,
            crashEndIndex,
            peakValue,
            crashSteepness
        };
    }

    /**
     * Simple slope calculation for a subset of values
     */
    private simpleSlope(values: number[]): number {
        if (values.length < 2) return 0;
        const n = values.length;
        return (values[n - 1] - values[0]) / (n - 1);
    }

    /**
     * Calculate the visual angle of the trend line in degrees.
     * This normalizes the coordinate space to a 1:1 aspect ratio so the angle
     * represents what you'd see on a square graph.
     * 
     * @param slope - The raw slope from linear regression
     * @param dataPoints - The data points (used for range calculation)
     * @returns Angle in degrees: -90° = vertical drop, 0° = flat, +90° = vertical rise
     */
    private calculateVisualAngle(slope: number, dataPoints: AnalyzerDataPoint[]): number {
        if (dataPoints.length < 2) return 0;
        
        const n = dataPoints.length;
        const values = dataPoints.map(d => d.value);
        const yMax = Math.max(...values);
        
        // ALWAYS use 0-based scale for algorithm consistency
        // The visual angle calculation should be deterministic:
        // same data = same angle = same condition, regardless of UI display settings
        // 
        // Using 0 to yMax ensures:
        // - Stable high values (90-92) show as nearly flat (~0°)
        // - A true decline (100 → 50) shows as steep (~-45°)
        // - Algorithm results don't change based on viewing preferences
        const yRange = yMax;
        
        // If all values are zero, it's undefined - treat as flat
        if (yRange === 0) return 0;
        
        // Normalize to 1:1 aspect ratio:
        // X goes from 0 to n-1, normalize to 0-1: x_norm = x / (n-1)
        // Y normalized from 0 to max value
        // 
        // Raw slope = Δy / Δx (in original units)
        // Normalized slope for visual = (Δy / yRange) / (Δx / (n-1))
        //                             = slope * (n - 1) / yRange
        
        const normalizedSlope = slope * (n - 1) / yRange;
        
        // Convert to angle in degrees
        const angleRadians = Math.atan(normalizedSlope);
        const angleDegrees = angleRadians * (180 / Math.PI);
        
        return angleDegrees;
    }

    /**
     * Analyze the ending portion of data (for Power detection)
     * Returns stats about the last X% of the data
     * @param overallMax The maximum value from the entire dataset (for threshold calculation)
     */
    private analyzeEndingPortion(dataPoints: AnalyzerDataPoint[], overallMax: number, portion = 0.4): {
        mean: number;
        cv: number;
        min: number;
        isStable: boolean;
        isHigh: boolean;
        minStaysHigh: boolean;
        slope: number;
    } {
        const startIndex = Math.floor(dataPoints.length * (1 - portion));
        const endingPoints = dataPoints.slice(startIndex);
        const values = endingPoints.map(dp => dp.value);
        
        const mean = values.reduce((a, b) => a + b, 0) / values.length;
        const min = Math.min(...values);
        const variance = values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / values.length;
        const stdDev = Math.sqrt(variance);
        const cv = mean !== 0 ? stdDev / mean : 0;
        
        // Calculate slope of ending portion
        const slope = this.simpleSlope(values);
        const normalizedSlope = mean !== 0 ? slope / mean : slope;
        
        // Calculate threshold as percentage of overall max
        const powerThreshold = overallMax * this.config.powerThreshold;
        
        return {
            mean,
            cv,
            min,
            isStable: cv < this.config.stabilityVariancePercent && Math.abs(normalizedSlope) < 0.01,
            isHigh: mean > powerThreshold,
            minStaysHigh: min >= powerThreshold, // No dips below power level in ending portion
            slope: normalizedSlope
        };
    }

    /**
     * Count consecutive trailing points that are in the power zone
     * Counts backwards from the end until a point drops below threshold
     */
    private countConsecutivePowerZonePoints(dataPoints: AnalyzerDataPoint[], overallMax: number): number {
        const powerThreshold = overallMax * this.config.powerThreshold;
        let count = 0;
        
        // Count from the end backwards
        for (let i = dataPoints.length - 1; i >= 0; i--) {
            if (dataPoints[i].value >= powerThreshold) {
                count++;
            } else {
                break; // Stop at first point below threshold
            }
        }
        
        return count;
    }

    /**
     * Calculate volatility score (0-1)
     */
    private calculateVolatility(values: number[], mean: number): number {
        if (values.length < 3 || mean === 0) return 0;

        // Count direction changes
        let directionChanges = 0;
        for (let i = 2; i < values.length; i++) {
            const prev = values[i - 1] - values[i - 2];
            const curr = values[i] - values[i - 1];
            if ((prev > 0 && curr < 0) || (prev < 0 && curr > 0)) {
                directionChanges++;
            }
        }

        // Normalize by possible changes
        const maxChanges = values.length - 2;
        const changeRatio = directionChanges / maxChanges;

        // Also factor in coefficient of variation
        const cv = Math.abs(this.calculateCV(values, mean));

        // Combine (weighted average)
        return Math.min(1, (changeRatio * 0.6) + (cv * 0.4));
    }

    private calculateCV(values: number[], mean: number): number {
        if (mean === 0) return 0;
        const variance = values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / values.length;
        return Math.sqrt(variance) / mean;
    }

    /**
     * Collect factors that will influence the condition determination
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private collectFactors(metrics: AnalysisMetrics, _dataPoints: AnalyzerDataPoint[]): ConditionFactor[] {
        const factors: ConditionFactor[] = [];

        // Factor 1: Slope direction and magnitude
        const normalizedSlope = metrics.mean !== 0 ? metrics.slope / metrics.mean : metrics.slope;
        factors.push({
            name: 'Slope (normalized)',
            value: normalizedSlope,
            weight: 0.3,
            supports: this.getSlopeSupports(normalizedSlope)
        });

        // Factor 2: Position relative to baseline
        factors.push({
            name: 'Distance from baseline',
            value: metrics.distanceFromBaseline,
            weight: 0.2,
            supports: this.getPositionSupports(metrics.distanceFromBaseline, metrics.mean, metrics.max)
        });

        // Factor 3: Stability (coefficient of variation)
        factors.push({
            name: 'Coefficient of Variation',
            value: metrics.coefficientOfVariation,
            weight: 0.2,
            supports: this.getStabilitySupports(metrics.coefficientOfVariation)
        });

        // Factor 4: Recovery pattern
        factors.push({
            name: 'Has Recovery',
            value: metrics.hasRecovery,
            weight: metrics.hasRecovery ? 0.25 : 0.05,
            supports: metrics.hasRecovery ? [Condition.EMERGENCY] : []
        });

        // Factor 5: Overall percent change
        factors.push({
            name: 'Percent Change',
            value: metrics.percentChange,
            weight: 0.15,
            supports: this.getChangeSupports(metrics.percentChange)
        });

        // Factor 6: Volatility
        factors.push({
            name: 'Volatility Score',
            value: metrics.volatilityScore,
            weight: 0.1,
            supports: metrics.volatilityScore > 0.5 ? [Condition.NORMAL] : []
        });

        return factors;
    }

    private getSlopeSupports(normalizedSlope: number): Condition[] {
        if (normalizedSlope < -this.config.sharpDropPercent) return [Condition.DANGER];
        if (normalizedSlope < -0.02) return [Condition.DANGER, Condition.EMERGENCY];
        if (normalizedSlope > this.config.sharpRisePercent) return [Condition.AFFLUENCE];
        if (normalizedSlope > 0.02) return [Condition.AFFLUENCE, Condition.NORMAL];
        return [Condition.NORMAL, Condition.POWER, Condition.NON_EXISTENCE];
    }

    private getPositionSupports(distance: number, mean: number, max: number): Condition[] {
        const powerThreshold = max * this.config.powerThreshold;
        if (mean < this.config.nonExistenceThreshold) return [Condition.NON_EXISTENCE];
        if (mean > powerThreshold) return [Condition.POWER];
        return [Condition.NORMAL, Condition.EMERGENCY, Condition.DANGER, Condition.AFFLUENCE];
    }

    private getStabilitySupports(cv: number): Condition[] {
        if (cv < this.config.stabilityVariancePercent) return [Condition.NORMAL, Condition.POWER];
        if (cv > 0.3) return [Condition.DANGER, Condition.EMERGENCY];
        return [Condition.NORMAL];
    }

    private getChangeSupports(percentChange: number): Condition[] {
        if (percentChange < -this.config.sharpDropPercent) return [Condition.DANGER];
        if (percentChange > this.config.sharpRisePercent) return [Condition.AFFLUENCE];
        return [Condition.NORMAL];
    }

    /**
     * Determine the overall trend direction
     */
    private determineTrendDirection(metrics: AnalysisMetrics): TrendDirection {
        // Use the visual angle directly - this matches what the user sees on the graph
        // Visual angle is already calculated based on 1:1 aspect ratio normalization
        const angle = metrics.visualAngle;
        
        // First check for clear directional trends - steep angles override volatility
        // A consistent decline/rise shouldn't be called "volatile" even with high CV
        const isSteepTrend = Math.abs(angle) > 20;
        
        // Only flag as volatile if:
        // 1. High volatility score AND
        // 2. NOT a steep consistent trend (which would have high CV but clear direction)
        if (metrics.volatilityScore > 0.6 && !isSteepTrend) return 'volatile';
        
        if (angle > 15) return 'up';      // Rising trend (> 15° upward)
        if (angle < -5) return 'down';    // Declining trend (> 5° downward)
        return 'stable';                   // Nearly flat (-5° to +15°)
    }

    /**
     * Main condition detection logic
     * 
     * DOWN-TREND CONDITIONS (using visual angle of trend line):
     * - Non-Existence: Steepest crash (angle < -60°), dropping from high to near-nothing
     * - Danger: Steep decline (angle between -30° and -60°)
     * - Emergency: Gradual decline (angle between -5° and -30°) OR recovery pattern
     * 
     * UP-TREND CONDITIONS:
     * - Affluence: Rising trend (angle > 15°)
     * - Power: Sustained high performance (stable at high values)
     * 
     * DEFAULT: Normal operation
     */
    private detectCondition(
        metrics: AnalysisMetrics, 
        factors: ConditionFactor[],
        trendDirection: TrendDirection,
        dataPoints: AnalyzerDataPoint[]
    ): { condition: Condition; confidence: number; explanation: string } {
        
        const normalizedSlope = metrics.mean !== 0 ? metrics.slope / metrics.mean : metrics.slope;
        const visualAngle = metrics.visualAngle;
        const values = dataPoints.map(d => d.value);
        
        // Calculate power threshold as percentage of max value
        const powerThreshold = metrics.max * this.config.powerThreshold;
        
        // Analyze ending portion for Power detection (last 40% of data)
        const endingAnalysis = this.analyzeEndingPortion(dataPoints, metrics.max, 0.4);
        
        // Detect Non-Existence crash pattern (steep crash then flatline)
        const nonExistenceCrash = this.detectNonExistenceCrash(values);
        
        // Decision tree approach with confidence scoring
        // Priority order: Non-Existence → Emergency (recovery) → Danger → Emergency (gradual) → Power → Affluence → Normal

        // ============================================================
        // 1. NON-EXISTENCE: Steep CRASH then flatline near zero
        // ============================================================
        // NOT a gradual decline over time - that's Danger or Emergency
        // This is a SUDDEN crash (over short period) then flatline at bottom
        if (nonExistenceCrash.isNonExistence) {
            const confidence = Math.min(95, 55 + nonExistenceCrash.crashSteepness * 10);
            return {
                condition: Condition.NON_EXISTENCE,
                confidence,
                explanation: `Crashed to non-existence. Activity peaked at ${nonExistenceCrash.peakValue.toFixed(1)}, ` +
                    `then crashed steeply (steepness: ${nonExistenceCrash.crashSteepness.toFixed(2)}) to near-zero and flatlined. ` +
                    `Ending mean: ${endingAnalysis.mean.toFixed(1)}.`
            };
        }

        // Also check for extremely low flat lines (already at zero)
        // This is Emergency (low crisis) not Non-Existence, unless there was a prior crash
        // Non-Existence requires the CRASH pattern - activity that was there and fell off

        // ============================================================
        // 1b. NON-EXISTENCE: Extremely steep angle (within non-existence zone)
        // ============================================================
        const isNonExistenceAngle = visualAngle >= this.config.angleNonExistenceMin && visualAngle < this.config.angleNonExistenceMax;
        if (isNonExistenceAngle) {
            const confidence = Math.min(95, 60 + Math.abs(visualAngle) * 0.5);
            return {
                condition: Condition.NON_EXISTENCE,
                confidence,
                explanation: `Extreme steep decline. Visual angle: ${visualAngle.toFixed(1)}° (non-existence zone: ${this.config.angleNonExistenceMin}° to ${this.config.angleNonExistenceMax}°). ` +
                    `Overall change: ${(metrics.percentChange * 100).toFixed(1)}%. Critical crash pattern.`
            };
        }

        // ============================================================
        // 2. EMERGENCY (Recovery Pattern): Drop then partial/full recovery
        // ============================================================
        // V-shape pattern - dropped but bouncing back
        const isHighValueOscillation = metrics.mean > powerThreshold * 0.8 && metrics.volatilityScore < 0.30;
        if (metrics.hasRecovery && metrics.recoveryPercent > 0.2 && !isHighValueOscillation) {
            const confidence = Math.min(95, 50 + metrics.recoveryPercent * 50);
            return {
                condition: Condition.EMERGENCY,
                confidence,
                explanation: `Recovery pattern detected (V-shape). Dropped to lowest point at index ${metrics.lowestPointIndex}, ` +
                    `then recovered ${(metrics.recoveryPercent * 100).toFixed(1)}% of the loss. Visual angle: ${visualAngle.toFixed(1)}°.`
            };
        }

        // ============================================================
        // 3. DANGER: Steep decline (angle within danger zone)
        // ============================================================
        const isDanger = visualAngle >= this.config.angleDangerMin && visualAngle < this.config.angleDangerMax;
        if (isDanger) {
            // Confidence based on how steep within the danger range
            const angleDistance = Math.abs(visualAngle - this.config.angleDangerMax);
            const confidence = Math.min(95, 60 + angleDistance * 1.5);
            return {
                condition: Condition.DANGER,
                confidence,
                explanation: `Steep decline detected. Visual angle: ${visualAngle.toFixed(1)}° (danger zone: ${this.config.angleDangerMin}° to ${this.config.angleDangerMax}°). ` +
                    `Overall change: ${(metrics.percentChange * 100).toFixed(1)}%. Things going wrong fast.`
            };
        }

        // ============================================================
        // 4. POWER: Sustained high performance (CHECK BEFORE EMERGENCY!)
        // ============================================================
        // IMPORTANT: Check power zone BEFORE emergency angles!
        // Auto-scaling can make small fluctuations at high levels look like "decline"
        // but if values are sustained in power zone, that's POWER not EMERGENCY.
        //
        // Power requires:
        // - High ending mean (above power threshold)
        // - Stable (low CV)
        // - Ending portion minimum stays above power threshold (no dips)
        // - Values actually in power zone (not just angle-based)
        if (endingAnalysis.isHigh && endingAnalysis.isStable && endingAnalysis.minStaysHigh) {
            // Even with slight negative angle (from auto-scale visual noise),
            // if values are high and stable, it's POWER
            const heightScore = endingAnalysis.mean / 100;
            const stabilityScore = 1 - endingAnalysis.cv;
            const confidence = Math.min(95, (heightScore * 50) + (stabilityScore * 50));
            return {
                condition: Condition.POWER,
                confidence,
                explanation: `Sustained high performance. Ending mean: ${endingAnalysis.mean.toFixed(2)}, CV: ${(endingAnalysis.cv * 100).toFixed(1)}%. ` +
                    `Ending min: ${endingAnalysis.min.toFixed(1)} (above ${powerThreshold.toFixed(1)} = ${(this.config.powerThreshold * 100).toFixed(0)}% of max). Visual angle: ${visualAngle.toFixed(1)}°. Stable at high level.`
            };
        }

        // Also check consecutive points in power zone (before emergency angle check)
        const consecutivePowerPoints = this.countConsecutivePowerZonePoints(dataPoints, metrics.max);
        const isStillInPowerZone = dataPoints[dataPoints.length - 1].value >= powerThreshold;
        
        if (consecutivePowerPoints >= this.config.minPowerZonePoints && isStillInPowerZone) {
            const heightScore = endingAnalysis.mean / 100;
            const sustainedScore = Math.min(1, consecutivePowerPoints / dataPoints.length);
            const confidence = Math.min(95, 60 + (heightScore * 20) + (sustainedScore * 20));
            return {
                condition: Condition.POWER,
                confidence,
                explanation: `Sustained in power zone. ${consecutivePowerPoints} consecutive points above ${(this.config.powerThreshold * 100).toFixed(0)}% threshold ` +
                    `(${powerThreshold.toFixed(1)}). Ending value: ${dataPoints[dataPoints.length - 1].value.toFixed(1)}. Visual angle: ${visualAngle.toFixed(1)}°. ` +
                    `Maintaining high performance regardless of growth rate.`
            };
        }

        // ============================================================
        // 5. EMERGENCY: Gradual decline (angle within emergency zone)
        // ============================================================
        // Only reach here if NOT in power zone
        const isEmergency = visualAngle >= this.config.angleEmergencyMin && visualAngle < this.config.angleEmergencyMax;
        if (isEmergency) {
            const confidence = Math.min(85, 55 + Math.abs(visualAngle) * 1.5);
            return {
                condition: Condition.EMERGENCY,
                confidence,
                explanation: `Gradual decline detected. Visual angle: ${visualAngle.toFixed(1)}° (emergency zone: ${this.config.angleEmergencyMin}° to ${this.config.angleEmergencyMax}°). ` +
                    `Overall change: ${(metrics.percentChange * 100).toFixed(1)}%. Needs attention before it becomes Danger.`
            };
        }

        // ============================================================
        // 5b. EMERGENCY: Declining from high position (even with gentle slope)
        // ============================================================
        // Started at power levels but now dropping
        const startedHigh = metrics.startValue > powerThreshold * 0.85;
        const isModerateDecline = metrics.percentChange < -0.15 && metrics.percentChange > -0.50;
        if (startedHigh && isModerateDecline && !metrics.hasRecovery) {
            const declineMagnitude = Math.abs(metrics.percentChange);
            const confidence = Math.min(90, 50 + declineMagnitude * 100);
            return {
                condition: Condition.EMERGENCY,
                confidence,
                explanation: `Declining from high position. Started at ${metrics.startValue.toFixed(1)}, now at ${metrics.endValue.toFixed(1)} ` +
                    `(${(metrics.percentChange * 100).toFixed(1)}%). Visual angle: ${visualAngle.toFixed(1)}°. Was performing well but now falling.`
            };
        }

        // ============================================================
        // 6. POWER: Extremely steep rise (angle within power zone)
        // ============================================================
        const isPowerAngle = visualAngle >= this.config.anglePowerMin && visualAngle <= this.config.anglePowerMax;
        if (isPowerAngle) {
            const confidence = Math.min(95, 70 + (visualAngle - this.config.anglePowerMin) * 0.5);
            return {
                condition: Condition.POWER,
                confidence,
                explanation: `Explosive growth detected. Visual angle: ${visualAngle.toFixed(1)}° (power zone: ${this.config.anglePowerMin}° to ${this.config.anglePowerMax}°). ` +
                    `Overall change: ${(metrics.percentChange * 100).toFixed(1)}%. Exceptional performance trajectory.`
            };
        }

        // ============================================================
        // 7. AFFLUENCE: Rising trend (angle within affluence zone)
        // ============================================================
        const isAffluence = visualAngle >= this.config.angleAffluenceMin && visualAngle < this.config.angleAffluenceMax;
        if (isAffluence || (metrics.percentChange > this.config.sharpRisePercent && !isPowerAngle)) {
            const riseMagnitude = Math.max(visualAngle, metrics.percentChange * 45); // Convert % to rough angle equivalent
            const confidence = Math.min(95, 55 + Math.abs(riseMagnitude));
            return {
                condition: Condition.AFFLUENCE,
                confidence,
                explanation: `Rising trend detected. Visual angle: ${visualAngle.toFixed(1)}° (affluence zone: ${this.config.angleAffluenceMin}° to ${this.config.angleAffluenceMax}°). ` +
                    `Overall change: ${(metrics.percentChange * 100).toFixed(1)}%. Things improving.`
            };
        }

        // Fallback Power check: overall high and stable (for flat high data)
        // Must also have ending portion minimum above power threshold (no dips)
        const isNotDecliningSlope = normalizedSlope > -0.005;
        const isNotDecliningOverall = metrics.percentChange > -0.10;
        const isTrulyStable = isNotDecliningSlope && isNotDecliningOverall;
        
        if (metrics.mean > powerThreshold && 
            metrics.coefficientOfVariation < this.config.stabilityVariancePercent &&
            isTrulyStable &&
            endingAnalysis.minStaysHigh) {
            const heightScore = metrics.mean / 100;
            const stabilityScore = 1 - metrics.coefficientOfVariation;
            const confidence = Math.min(95, (heightScore * 50) + (stabilityScore * 50));
            return {
                condition: Condition.POWER,
                confidence,
                explanation: `Sustained high performance. Mean: ${metrics.mean.toFixed(2)}, CV: ${(metrics.coefficientOfVariation * 100).toFixed(1)}%. ` +
                    `Ending min: ${endingAnalysis.min.toFixed(1)} (above ${powerThreshold.toFixed(1)}). Visual angle: ${visualAngle.toFixed(1)}°. Stable at high level.`
            };
        }

        // ============================================================
        // 8. NORMAL: Default - angle within normal zone or no other condition matched
        // ============================================================
        // Normal zone: angleNormalMin to angleNormalMax (fallback for any unmatched angle)
        const stabilityScore = 1 - metrics.coefficientOfVariation;
        const flatnessScore = 1 - Math.abs(normalizedSlope) * 10;
        const confidence = Math.min(90, Math.max(50, (stabilityScore * 50) + (flatnessScore * 50)));
        
        return {
            condition: Condition.NORMAL,
            confidence,
            explanation: `Stable operation. Mean: ${metrics.mean.toFixed(2)}, CV: ${(metrics.coefficientOfVariation * 100).toFixed(1)}%, ` +
                `Visual angle: ${visualAngle.toFixed(1)}° (normal zone: ${this.config.angleNormalMin}° to ${this.config.angleNormalMax}°). No significant trend.`
        };
    }

    /**
     * Calculate confidence score based on how far a value is from threshold
     */
    private calculateConfidence(value: number, threshold: number, maxValue: number, inverse: boolean): number {
        const range = maxValue - threshold;
        const position = inverse ? (threshold - value) : (value - threshold);
        const ratio = Math.min(1, Math.max(0, position / range));
        return Math.min(95, 50 + ratio * 45);
    }

    /**
     * Generate trend line points for visualization
     */
    private generateTrendLine(dataPoints: AnalyzerDataPoint[], metrics: AnalysisMetrics): { x: Date; y: number }[] {
        return dataPoints.map((point, index) => ({
            x: point.date,
            y: metrics.intercept + metrics.slope * index
        }));
    }

    /**
     * Create an unknown result for edge cases
     */
    private createUnknownResult(dataPoints: AnalyzerDataPoint[], reason: string): ConditionAnalysisResult {
        return {
            condition: Condition.UNKNOWN,
            conditionInfo: CONDITION_INFO[Condition.UNKNOWN],
            confidence: 0,
            trendDirection: 'stable',
            metrics: this.getEmptyMetrics(),
            explanation: reason,
            factors: [],
            dataPoints: dataPoints || [],
            trendLine: []
        };
    }

    private getEmptyMetrics(): AnalysisMetrics {
        return {
            slope: 0, intercept: 0, rSquared: 0,
            mean: 0, median: 0, variance: 0, standardDeviation: 0, coefficientOfVariation: 0,
            min: 0, max: 0, range: 0,
            startValue: 0, endValue: 0, absoluteChange: 0, percentChange: 0, rateOfChange: 0,
            distanceFromBaseline: 0, percentOfMax: 0,
            hasRecovery: false, recoveryStartIndex: null, recoveryPercent: 0,
            lowestPointIndex: 0, highestPointIndex: 0,
            volatilityScore: 0,
            visualAngle: 0
        };
    }
}
